/**
 * Стейт пост-регистрационного онбординга — обёртка над
 * /api/v1/onboarding/state. Лениво подгружается при первом обращении
 * и используется как редактором (на /welcome), так и гейтом
 * (редирект на /welcome из MainLayout).
 *
 * Бэкенд: fastapi_app/api/v1/onboarding_state.py.
 */
import { makeAutoObservable, runInAction } from 'mobx';
import { api } from '../../shared/api/api';

export type PmsMode = 'undecided' | 'pms_connected' | 'standalone';

export interface ModulesSelected {
  concierge: boolean;
  housekeeping: boolean;
}

export interface OnboardingSteps {
  // Concierge (6)
  concierge_schema_opened: boolean;
  concierge_catalog_ready: boolean;
  concierge_payment_configured: boolean;
  concierge_notifications_configured: boolean;
  concierge_responsible_assigned: boolean;
  concierge_test_order_completed: boolean;
  // Housekeeping (4)
  hk_rooms_configured: boolean;
  hk_checklist_ready: boolean;
  hk_housekeepers_added: boolean;
  hk_autogen_enabled: boolean;
}

export type StepKey = keyof OnboardingSteps;

export interface OnboardingState {
  platform_id: number;
  pms_mode: PmsMode;
  modules_selected: ModulesSelected;
  steps: OnboardingSteps;
  concierge_ready: boolean;
  housekeeping_ready: boolean;
  is_completed: boolean;
  welcome_seen_at: string | null;
  completed_at: string | null;
  skipped_at: string | null;
}

const ENDPOINT = '/api/v1/onboarding/state';

export class OnboardingStateStore {
  state: OnboardingState | null = null;
  isLoading = false;
  isSaving = false;
  error: string | null = null;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  /**
   * Не загружен и не загружается. Используется gate'ом, чтобы инициировать
   * первую загрузку только один раз.
   */
  get isUnfetched(): boolean {
    return this.state === null && !this.isLoading && this.error === null;
  }

  /**
   * Нужен ли принудительный редирект на /welcome:
   *   - стейт загружен;
   *   - онбординг не завершён и не пропущен.
   */
  get shouldRedirectToWelcome(): boolean {
    if (!this.state) return false;
    return !this.state.is_completed && !this.state.skipped_at;
  }

  async fetch() {
    this.isLoading = true;
    this.error = null;
    try {
      const { data } = await api.get<OnboardingState>(ENDPOINT);
      runInAction(() => {
        this.state = data;
        this.isLoading = false;
      });
    } catch (err: any) {
      runInAction(() => {
        this.error = err?.response?.data?.detail?.message
          || err?.message
          || 'Не удалось загрузить состояние онбординга';
        this.isLoading = false;
      });
    }
  }

  /** PUT-эквивалент: pms_mode и/или modules_selected. */
  async update(body: {
    pms_mode?: PmsMode;
    modules_selected?: ModulesSelected;
  }) {
    this.isSaving = true;
    try {
      const { data } = await api.post<OnboardingState>(ENDPOINT, body);
      runInAction(() => {
        this.state = data;
        this.isSaving = false;
      });
    } catch (err: any) {
      runInAction(() => {
        this.error = err?.response?.data?.detail?.message
          || err?.message
          || 'Не удалось сохранить';
        this.isSaving = false;
      });
    }
  }

  async setStep(step: StepKey, completed = true) {
    this.isSaving = true;
    try {
      const { data } = await api.post<OnboardingState>(
        `${ENDPOINT}/step`,
        { step, completed },
      );
      runInAction(() => {
        this.state = data;
        this.isSaving = false;
      });
    } catch (err: any) {
      runInAction(() => {
        this.error = err?.message || 'Не удалось обновить шаг';
        this.isSaving = false;
      });
    }
  }

  async markWelcomeSeen() {
    try {
      const { data } = await api.post<OnboardingState>(
        `${ENDPOINT}/welcome-seen`,
      );
      runInAction(() => { this.state = data; });
    } catch (_e) {
      // не критично, повторим на следующем визите
    }
  }

  async markCompleted() {
    this.isSaving = true;
    try {
      const { data } = await api.post<OnboardingState>(
        `${ENDPOINT}/complete`,
      );
      runInAction(() => {
        this.state = data;
        this.isSaving = false;
      });
    } catch (err: any) {
      runInAction(() => {
        this.error = err?.message || 'Не удалось завершить онбординг';
        this.isSaving = false;
      });
    }
  }

  async markSkipped() {
    this.isSaving = true;
    try {
      const { data } = await api.post<OnboardingState>(
        `${ENDPOINT}/skip`,
      );
      runInAction(() => {
        this.state = data;
        this.isSaving = false;
      });
    } catch (err: any) {
      runInAction(() => {
        this.error = err?.message || 'Не удалось пропустить';
        this.isSaving = false;
      });
    }
  }

  reset() {
    this.state = null;
    this.isLoading = false;
    this.isSaving = false;
    this.error = null;
  }
}
